import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Search, MapPin, RefreshCw, Star } from "lucide-react";
import { useAuth, API } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUSES = [
  { v: "all", label: "Alle" },
  { v: "pending", label: "In afwachting" },
  { v: "approved", label: "Goedgekeurd" },
  { v: "rejected", label: "Afgewezen" },
];
const badge = (s) =>
  s === "approved" ? "bg-[#1B6B2E] text-white" : s === "rejected" ? "bg-[#B71C1C] text-white" : "bg-[#F4D03F] text-[#0D3B4C]";
const label = (s) => ({ approved: "Goedgekeurd", rejected: "Afgewezen", pending: "In afwachting" }[s] || s);

export const ToriModeration = ({ onAuthError }) => {
  const { authHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/tori`, {
        headers: authHeader(),
        params: { status, search },
      });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthError?.();
      else toast.error("Inzendingen laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [authHeader, status, search, onAuthError]);

  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatusFor = async (id, newStatus) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/tori/${id}/status`, { status: newStatus }, { headers: authHeader() });
      toast.success(newStatus === "approved" ? "Goedgekeurd — nu live." : "Afgewezen.");
      load();
    } catch (err) {
      if (err?.response?.status === 401) onAuthError?.();
      else toast.error("Bijwerken mislukt.");
    } finally {
      setBusy("");
    }
  };

  const toggleFeatured = async (id, featured) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/tori/${id}/feature`, { featured }, { headers: authHeader() });
      toast.success(featured ? "Ingesteld als Tori van de week." : "Uitgelicht verwijderd.");
      load();
    } catch (err) {
      if (err?.response?.status === 401) onAuthError?.();
      else toast.error(err?.response?.data?.detail || "Bijwerken mislukt.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-4 mb-6 bg-[#FEF9E7] p-4">
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="flex gap-2 flex-1 min-w-[240px]"
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="tori-search"
            placeholder="Zoek op naam, verhaal of locatie..."
            className="flex-1 border-2 border-[#0D3B4C] px-4 py-2 font-[400] focus:border-[#B71C1C] focus:outline-none"
          />
          <button type="submit" data-testid="tori-search-btn" className="bg-[#1B6B2E] text-white font-[700] px-4 py-2 flex items-center gap-2 hover:bg-[#144F22] transition-colors">
            <Search size={16} /> Zoek
          </button>
        </form>
        <div className="flex flex-col gap-1">
          <label className="font-[600] text-[#0D3B4C] text-sm">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="tori-status-filter"
            className="border-2 border-[#0D3B4C] bg-white px-3 py-2 font-[600] text-[#0D3B4C] focus:border-[#B71C1C] focus:outline-none"
          >
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>
        <button onClick={load} data-testid="tori-refresh" className="flex items-center gap-2 bg-[#E8F5E9] text-[#0D3B4C] font-[700] px-4 py-2 hover:bg-[#d4ecd7] transition-colors">
          <RefreshCw size={16} /> Vernieuwen
        </button>
      </div>

      {loading ? (
        <p className="font-[600] text-[#0D3B4C]">Laden...</p>
      ) : items.length === 0 ? (
        <p className="font-[600] text-[#0D3B4C]" data-testid="no-tori">Geen inzendingen gevonden.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="tori-list">
          {items.map((t) => (
            <div key={t.id} className="border-2 border-[#E8F5E9] bg-white p-5" data-testid={`tori-item-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-[700] text-[#0D3B4C]">{t.name}</h3>
                  {t.email && <p className="text-xs text-[#0D3B4C]">{t.email}</p>}
                  {t.location_text && (
                    <p className="text-sm font-[600] text-[#1B6B2E] flex items-center gap-1">
                      <MapPin size={14} /> {t.location_text}
                    </p>
                  )}
                  {t.locality && (
                    <p className="text-xs text-[#1B6B2E]">Plaats (kaart): {t.locality}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`px-2 py-1 font-[700] text-xs ${badge(t.status)}`}>{label(t.status)}</span>
                  {t.featured && (
                    <span className="px-2 py-1 font-[700] text-xs bg-[#F4D03F] text-[#0D3B4C] flex items-center gap-1">
                      <Star size={12} className="fill-[#0D3B4C]" /> Tori v/d week
                    </span>
                  )}
                </div>
              </div>
              {t.photos && t.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {t.photos.map((p) => (
                    <div key={p} className="aspect-square bg-[#E8F5E9] overflow-hidden">
                      <img src={`${BACKEND}${p}`} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
              <p className="text-sm font-[400] text-[#0D3B4C] mt-3">{t.story}</p>
              {typeof t.lat === "number" && (
                <p className="text-xs text-[#0D3B4C] mt-2">Pin: {t.lat.toFixed(4)}, {t.lng.toFixed(4)}</p>
              )}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => setStatusFor(t.id, "approved")}
                  disabled={busy === t.id || t.status === "approved"}
                  data-testid={`approve-${t.id}`}
                  className="flex items-center gap-2 bg-[#1B6B2E] text-white font-[700] px-4 py-2 hover:bg-[#144F22] transition-colors disabled:opacity-40"
                >
                  <Check size={16} /> Goedkeuren
                </button>
                <button
                  onClick={() => setStatusFor(t.id, "rejected")}
                  disabled={busy === t.id || t.status === "rejected"}
                  data-testid={`reject-${t.id}`}
                  className="flex items-center gap-2 bg-[#B71C1C] text-white font-[700] px-4 py-2 hover:bg-[#8E1515] transition-colors disabled:opacity-40"
                >
                  <X size={16} /> Afwijzen
                </button>
              </div>
              <div
                className={`flex items-center justify-between gap-3 mt-4 pt-3 border-t border-[#E8F5E9] ${t.status === "approved" ? "" : "opacity-50"}`}
                title={t.status === "approved" ? "" : "Keur het verhaal eerst goed"}
              >
                <span className="flex items-center gap-2 font-[600] text-[#0D3B4C]">
                  <Star size={16} className="text-[#F4D03F] fill-[#F4D03F]" /> Uitgelicht op homepage
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!t.featured}
                  disabled={t.status !== "approved" || busy === t.id}
                  onClick={() => toggleFeatured(t.id, !t.featured)}
                  data-testid={`feature-${t.id}`}
                  className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${t.featured ? "bg-[#1B6B2E]" : "bg-[#cbd5cb]"} ${t.status === "approved" ? "cursor-pointer" : "cursor-not-allowed"}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${t.featured ? "translate-x-7" : "translate-x-0"}`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
